import { useQuery } from '@tanstack/react-query';
import { extract } from 'letterparser';
import { memo, useMemo } from 'react';
import { Letter } from 'react-letter';

import AvatarSVG from '@/assets/avatar.svg?react';
import { DateTime, Heading, Loading, Separator, Text } from '@/components/core';
import { toDocumentId, toFileId, useGetDocument, useGetFile } from '@/hooks/api';

import { Panel } from './Panel.tsx';

export type DetailsEmailPanelProps = {
  documentId: string;
};

const IDetailsEmailPanel = ({ documentId }: DetailsEmailPanelProps) => {
  const { data: doc } = useGetDocument(toDocumentId(documentId));
  const { data: blob } = useGetFile(toFileId(doc?.fileUrl));

  const { data: emailStr } = useQuery({
    queryKey: [documentId, 'text'],
    queryFn: async () => {
      if (!blob) throw Error('Blob is undefined');
      return await blob.text();
    },
  });

  const email = useMemo(() => (emailStr ? extract(emailStr) : undefined), [emailStr]);

  return (
    <Panel className="overflow-auto">
      <div className="flex flex-col p-4">
        <div className="flex flex-col gap-4 rounded-md border border-gray-200 bg-gray-50 p-4">
          <Heading className="!font-medium" size="lg">{email?.subject}</Heading>
          <div className="flex items-center gap-4">
            <AvatarSVG />
            <div className="flex flex-col">
              <Heading>{email?.from?.name}</Heading>
              <Text>{email?.from?.address}</Text>
            </div>
            <div className="grow" />
            <DateTime date={email?.date} />
          </div>
          <Separator orientation="horizontal" />
          {email?.html && email?.text ? (
            <Letter className="overflow-auto p-1" html={email.html} text={email.text} />
          ) : (
            <Loading label="Loading email..." />
          )}
        </div>
      </div>
    </Panel>
  );
};

export const DetailsEmailPanel = memo(IDetailsEmailPanel) as typeof IDetailsEmailPanel;
